/** The dual-grid corner-mask terrain model, and nothing that touches a GPU.
 *
 * `docs/specs/presentation.md` and `TILES/manifest.json` are authoritative; the
 * reference implementation is `TILES/tools/render_map.py`. The short version:
 *
 *   Terrain is stored per WORLD cell. What is drawn is DISPLAY cells, offset
 *   half a tile, so display cell (dy, dx) is bounded by the four world cells
 *   (dy-1,dx-1) (dy-1,dx) (dy,dx) (dy,dx-1) as its TL, TR, BR, BL corners.
 *
 *   One distinct terrain among the four -> that terrain's fill tile. Two or
 *   more -> start from the lowest-priority terrain's fill and composite each
 *   higher terrain t through the 16-entry corner mask whose bits are set where
 *   the corner's terrain has priority >= priority(t).
 *
 * The positional variant (dy mod 4, dx mod 4) is mandatory. Both the fills and
 * the masks are indexed by it, which is what makes a transition tile's solid
 * region pixel-identical to the fill that would otherwise have been there - and
 * what stops a field of grass reading as a repeating stamp.
 *
 * Known deviation from the reference, recorded honestly: for three or more
 * terrains in one display cell the reference resolves per pixel against a
 * perturbation field, because layering pre-thresholded masks in priority order
 * over-covers and leaves a collar of the wrong terrain (~19% of one quadrant)
 * where a mid-priority terrain sits diagonally opposite a higher one. This
 * module implements the layered rule the presentation spec states. Three-corner
 * junctions are rare and the artefact is a ~20px band; `world/terrain.ts`
 * documents where the per-pixel path would slot in.
 */

/** Must match `TERRAINS` in `@game/core`; `test/world.spec.ts` asserts it. */
export const TERRAIN_IDS = [
  "water", "sand", "dirt", "grass", "snow", "swamp", "rough", "cavern", "lava", "rock",
  "highlands", "wasteland",
] as const;
export type TerrainId = (typeof TERRAIN_IDS)[number];

/** ASSET_SPEC 2, global and fixed. Higher paints over lower. */
export const TERRAIN_PRIORITY: readonly number[] =
  Object.freeze([0, 10, 20, 30, 35, 40, 45, 50, 60, 90, 32, 47]);

export const VARIANT_PERIOD = 4;
export const VARIANT_COUNT = VARIANT_PERIOD * VARIANT_PERIOD;

/** 32x32 display cells per cached chunk, per `docs/specs/presentation.md`. */
export const CHUNK_CELLS = 32;

const mod4 = (n: number): number => ((n % VARIANT_PERIOD) + VARIANT_PERIOD) % VARIANT_PERIOD;

/** The positional variant of a display cell: `(dy mod 4) * 4 + (dx mod 4)`. */
export const variantIndex = (dy: number, dx: number): number =>
  mod4(dy) * VARIANT_PERIOD + mod4(dx);

/** Corner bit order is (TL, TR, BR, BL), LSB = TL, matching the mask atlas. */
export const cornerMaskIndex = (tl: number, tr: number, br: number, bl: number): number =>
  (tl & 1) | ((tr & 1) << 1) | ((br & 1) << 2) | ((bl & 1) << 3);

export const terrainPriority = (t: number): number => TERRAIN_PRIORITY[t] ?? 0;

export const terrainName = (t: number): TerrainId => TERRAIN_IDS[t] ?? "rock";

// ---------------------------------------------------------------- resolution

export interface TerrainLayer {
  /** Terrain index being painted. */
  readonly terrain: number;
  /** Corner mask index 1..15 it is painted through. */
  readonly mask: number;
}

export interface DisplayPlan {
  readonly corners: readonly [number, number, number, number];
  /** Distinct corner terrains, ascending priority. Length 1..4. */
  readonly present: readonly number[];
  /** The fill the cell starts from: the lowest-priority terrain present. */
  readonly base: number;
  /** Higher terrains, ascending priority, each with the mask it paints through. */
  readonly layers: readonly TerrainLayer[];
}

/**
 * Resolve one display cell from its four corner terrains.
 *
 * Ascending priority, and the mask for terrain `t` sets a corner when that
 * corner's terrain has priority *greater than or equal to* `t`. The `>=` is not
 * a typo: it is what makes the region a higher terrain will later paint over
 * belong to `t` in the meantime, so no gap of the base colour survives between
 * two layers.
 */
export function resolveDisplayCell(
  tl: number, tr: number, br: number, bl: number,
): DisplayPlan {
  const corners: readonly [number, number, number, number] = [tl, tr, br, bl];

  const distinct: number[] = [];
  for (const c of corners) if (!distinct.includes(c)) distinct.push(c);
  // Ties are impossible - two distinct terrains never share a priority - but
  // sort on the index as a second key anyway so the order is total.
  distinct.sort((a, b) => (terrainPriority(a) - terrainPriority(b)) || (a - b));

  const base = distinct[0] ?? 0;
  const layers: TerrainLayer[] = [];
  for (let i = 1; i < distinct.length; i++) {
    const t = distinct[i]!;
    const p = terrainPriority(t);
    const mask = cornerMaskIndex(
      terrainPriority(tl) >= p ? 1 : 0,
      terrainPriority(tr) >= p ? 1 : 0,
      terrainPriority(br) >= p ? 1 : 0,
      terrainPriority(bl) >= p ? 1 : 0,
    );
    layers.push({ terrain: t, mask });
  }
  return { corners, present: distinct, base, layers };
}

/** Cache key for a composited tile. Corners plus variant determine it entirely. */
export const displayPlanKey = (
  tl: number, tr: number, br: number, bl: number, variant: number,
): string => `${tl}.${tr}.${br}.${bl}.${variant}`;

// ---------------------------------------------------------------- atlas keys

const variantSuffix = (variant: number): string =>
  `${Math.floor(variant / VARIANT_PERIOD)}_${variant % VARIANT_PERIOD}`;

export const fillKey = (terrain: number, variant: number): string =>
  `terrain.${terrainName(terrain)}.fill.${variantSuffix(variant)}`;

export const maskKey = (mask: number, variant: number): string =>
  `terrain.mask.${mask}.${variantSuffix(variant)}`;

/**
 * The 16 pre-baked pair atlases. They are a *cache* of the two-terrain case and
 * nothing more, but using one turns a two-draw composite into a single sprite,
 * so the common coastline is drawn straight from the atlas.
 */
export const PREBAKED_PAIRS: ReadonlySet<string> = new Set([
  "sand__water", "dirt__sand", "grass__dirt", "snow__dirt", "swamp__dirt",
  "rough__dirt", "cavern__dirt", "lava__dirt", "grass__sand", "snow__sand",
  "swamp__sand", "rough__sand", "cavern__sand", "lava__sand", "rock__sand",
  "rock__water",
]);

/** `null` when the pair was not pre-baked and must be composited. */
export function transitionKey(
  over: number, under: number, mask: number, variant: number,
): string | null {
  const pair = `${terrainName(over)}__${terrainName(under)}`;
  if (!PREBAKED_PAIRS.has(pair)) return null;
  return `terrain.trans.${pair}.${mask}.${variantSuffix(variant)}`;
}

export const ROAD_KIND_IDS = ["none", "dirt", "gravel", "cobble"] as const;
export const RIVER_KIND_IDS = ["none", "water", "ice", "lava", "mud"] as const;

/**
 * The four cardinal masks that are a 90-degree corner, and for each of them the
 * two corner neighbours that would continue the turn as a 45-degree staircase.
 *
 * The classic decides a road tile's frame on the four-neighbourhood alone in nine of
 * its ten patterns; the tenth - `?-+ / -++ / ++?` in `CDrawRoadsOperation.cpp`,
 * documented in `TILES/docs/research-roads-rivers.md` section 6 - also reads two
 * CORNERS, and it is the one that gives a hand-drawn staircase its own art. A
 * tile that turns from E to S is drawn bowed out to its corner, not as an
 * elbow, when the tile to its NE and the tile to its SW also carry road: those
 * are the two cells the run continues into, one per arm.
 *
 * `arm1`/`arm2` are the two cardinal bits of the corner; `c1`/`c2` are the
 * offsets of the corner cell that continues that arm. The continuation of the
 * arm pointing `d` is the corner between `d` and the direction OPPOSITE the
 * other arm - a staircase alternates, so it leaves the E arm going north when
 * the other arm points south.
 */
interface CurveCase {
  readonly mask: number;
  readonly c1: readonly [number, number];
  readonly c2: readonly [number, number];
}

/** Indexed by orientation 0..3. `STAIR_BASE + o*4 + corners` is the atlas code. */
export const CURVE_CASES: readonly CurveCase[] = Object.freeze([
  // N+E, continuing NW off the N arm and SE off the E arm
  { mask: 1 | 2, c1: [-1, -1], c2: [1, 1] },
  // E+S, continuing NE off the E arm and SW off the S arm
  { mask: 2 | 4, c1: [1, -1], c2: [-1, 1] },
  // S+W, continuing SE off the S arm and NW off the W arm
  { mask: 4 | 8, c1: [1, 1], c2: [-1, -1] },
  // W+N, continuing SW off the W arm and NE off the N arm
  { mask: 8 | 1, c1: [-1, 1], c2: [1, -1] },
]);

/**
 * Bits 4-7 of a connection mask: a DIAGONAL arm to the corner neighbour.
 *
 * The overlay planes are eight-connected. The generator routes roads and
 * rivers on eight neighbours, so two road cells that share only a corner are a
 * road, and the tile draws a band from its centre to that corner - the
 * `diag.png` atlas beside each overlay's cardinal atlas, whose cells carry an
 * overhang so the band's wedge lands in the two flanking cells.
 *
 * The rule (mirrored by `render_mask` in `TILES/tools/build_overlays.py`,
 * which drives the mosaic seam check): the arm to a corner is reported only
 * when the corner cell carries the overlay AND NEITHER of the two cardinal
 * cells flanking that corner does. If a flank is paved the corner cell is
 * already reached through it and a second, diagonal band would fill the
 * triangle between them into a blob; and because the condition is symmetric
 * in the two cells, both ends of a diagonal join always agree that it exists.
 * 47 of the 256 combinations survive the rule; those are the shapes built.
 */
export const DIAG_NE = 16;
export const DIAG_SE = 32;
export const DIAG_SW = 64;
export const DIAG_NW = 128;
export const DIAG_ANY = DIAG_NE | DIAG_SE | DIAG_SW | DIAG_NW;

/**
 * Staircase corner bits, above the diagonal arms: bit 8 says the first arm of
 * a corner mask continues diagonally, bit 9 the second. Reported only on a
 * bare 90-degree corner with no diagonal arm of its own.
 */
export const DIAG_A = 256;
export const DIAG_B = 512;

/**
 * Atlas code space for `diag.png`: a diagonal-arm shape is its own mask value
 * (0..255); a staircase elbow is `STAIR_BASE + orientation * 4 + corners`.
 */
export const STAIR_BASE = 256;

/** Orientation index of a cardinal mask that is a 90-degree corner, or -1. */
export const curveOrientation = (cardinal: number): number => {
  for (let o = 0; o < CURVE_CASES.length; o++) {
    if (CURVE_CASES[o]!.mask === (cardinal & 15)) return o;
  }
  return -1;
};

/**
 * The `diag.png` column code for a connection mask, or -1 when the plain
 * sixteen-column atlas draws it. Pure so the packer's key census and the
 * builder's column list can be checked against it.
 */
export const diagCode = (connections: number): number => {
  const cardinal = connections & 15;
  const diag = connections & DIAG_ANY;
  if (diag !== 0) return cardinal | diag;
  const corners = ((connections & DIAG_A) !== 0 ? 1 : 0) | ((connections & DIAG_B) !== 0 ? 2 : 0);
  if (corners !== 0) {
    const o = curveOrientation(cardinal);
    // Corner bits on a mask that is not a corner would be a caller bug; fall
    // through to the cardinal atlas rather than index a column that is not there.
    if (o >= 0) return STAIR_BASE + o * 4 + corners;
  }
  return -1;
};

/**
 * The overlay atlas key for one tile.
 *
 * `connections` is what `connectionMask` returned: bits 0-3 are N/E/S/W and
 * select a column of the plain sixteen-column atlas; bits 4-7 (diagonal arms)
 * and 8-9 (staircase corners) send the tile to the `_diag` atlas instead,
 * keyed by `diagCode`.
 */
export const overlayKey = (
  set: "road" | "river", kind: number, connections: number, variant: number,
): string | null => {
  const name = set === "road" ? ROAD_KIND_IDS[kind] : RIVER_KIND_IDS[kind];
  if (name === undefined || name === "none") return null;
  const code = diagCode(connections);
  if (code >= 0) {
    return `terrain.overlay.${set}_${name}_diag.${code}.${variantSuffix(variant)}`;
  }
  return `terrain.overlay.${set}_${name}.${connections & 15}.${variantSuffix(variant)}`;
};

/**
 * Which of a cell's eight neighbours carry the same overlay family, as the
 * atlas wants to hear it: N/E/S/W in bits 0-3; a corner in bits 4-7 only when
 * both cells flanking it are bare (see `DIAG_NE`); and on a bare 90-degree
 * corner, and only there, the two staircase bits.
 *
 * The staircase bits are deliberately not reported for any other cardinal
 * mask, nor beside a diagonal arm. Nothing downstream has art for those, and a
 * bit nobody reads is a bit that will eventually be read wrongly.
 */
export function connectionMask(
  plane: Uint8Array, w: number, h: number, x: number, y: number, base = 0,
): number {
  const at = (cx: number, cy: number): number =>
    cx < 0 || cy < 0 || cx >= w || cy >= h ? 0 : (plane[base + cy * w + cx] ?? 0);
  const n = at(x, y - 1) !== 0, e = at(x + 1, y) !== 0;
  const s = at(x, y + 1) !== 0, ww = at(x - 1, y) !== 0;
  const cardinal = (n ? 1 : 0) | (e ? 2 : 0) | (s ? 4 : 0) | (ww ? 8 : 0);
  let diag = 0;
  if (!n && !e && at(x + 1, y - 1) !== 0) diag |= DIAG_NE;
  if (!e && !s && at(x + 1, y + 1) !== 0) diag |= DIAG_SE;
  if (!s && !ww && at(x - 1, y + 1) !== 0) diag |= DIAG_SW;
  if (!ww && !n && at(x - 1, y - 1) !== 0) diag |= DIAG_NW;
  if (diag !== 0) return cardinal | diag;
  const o = curveOrientation(cardinal);
  if (o < 0) return cardinal;
  const cse = CURVE_CASES[o]!;
  return cardinal
    | (at(x + cse.c1[0], y + cse.c1[1]) !== 0 ? DIAG_A : 0)
    | (at(x + cse.c2[0], y + cse.c2[1]) !== 0 ? DIAG_B : 0);
}

// ---------------------------------------------------------------- mouths

/**
 * The eight arm directions clockwise from north as (dx, dy, connection bit),
 * in the order `connectionMask` reports them and the atlas names them.
 */
export const ARM_DIRS: readonly (readonly [number, number, number])[] = Object.freeze([
  [0, -1, 1], [1, -1, DIAG_NE], [1, 0, 2], [1, 1, DIAG_SE],
  [0, 1, 4], [-1, 1, DIAG_SW], [-1, 0, 8], [-1, -1, DIAG_NW],
]);

/** Terrain index of water in `TERRAIN_IDS`. */
const WATER = 0;

/**
 * The extra arm a river's MOUTH carries into the sea, as a connection bit, or
 * 0 for a cell that is not a mouth.
 *
 * The generator ends a river on the last land cell before the water, and the
 * dead-end frame stops at that cell's centre - while the coast transition of
 * the dual grid paints the beach most of the way across the neighbouring
 * water cell. On screen the channel dried up a tile short of the sea. So a
 * dead end (exactly one arm) beside water grows an arm toward the water
 * cell, and the water cell draws the matching single-arm frame back
 * (`riverMouthsInto`), so the band runs edge-registered through the cell
 * boundary and ends in the water where the shoreline is drawn.
 *
 * Which water cell: the one most nearly continuing the channel, cardinal
 * before corner on a tie. Never one within 45 degrees of the arm the river
 * arrives on - the atlas has no frame for a hairpin (a corner arm beside its
 * own flank), and a river does not double back at the sea. A corner mouth
 * follows the corner-arm rule: both flanking cells must be bare of river,
 * which a dead end's are by construction, since its one river neighbour is
 * at least 90 degrees from the mouth.
 *
 * Not modelled by `render_mask` in build_overlays.py: the mosaic check has
 * no terrain, and a mouth is an ordinary one- or two-arm frame either way.
 */
export function riverMouth(
  river: Uint8Array, terrain: Uint8Array, visibility: Uint8Array,
  w: number, h: number, x: number, y: number, base = 0,
): number {
  const idx = (cx: number, cy: number): number =>
    cx < 0 || cy < 0 || cx >= w || cy >= h ? -1 : base + cy * w + cx;
  if ((river[idx(x, y)] ?? 0) === 0) return 0;
  const mask = connectionMask(river, w, h, x, y, base) & 255;
  if (mask === 0 || (mask & (mask - 1)) !== 0) return 0;
  let arm = -1;
  for (let k = 0; k < 8; k++) if (ARM_DIRS[k]![2] === mask) arm = k;
  if (arm < 0) return 0;
  let best = 0, bestScore = -1;
  for (let k = 0; k < 8; k++) {
    const turn = Math.abs(k - arm);
    const apart = turn > 4 ? 8 - turn : turn;
    if (apart < 2) continue;
    const [dx, dy, bit] = ARM_DIRS[k]!;
    const i = idx(x + dx, y + dy);
    if (i < 0 || (visibility[i] ?? 0) === 0 || (terrain[i] ?? 0) !== WATER || (river[i] ?? 0) !== 0) continue;
    // straight on scores 4, a 135-degree turn 3, a right angle 2; cardinal
    // wins the tie by the half point
    const score = apart * 2 + (k % 2 === 0 ? 1 : 0);
    if (score > bestScore) { bestScore = score; best = bit; }
  }
  return best;
}

/**
 * The river frames a WATER cell draws for the mouths that flow into it: one
 * (kind, single-arm connection bit) per neighbouring mouth whose `riverMouth`
 * arm points at this cell. Usually none; occasionally two.
 */
export function riverMouthsInto(
  river: Uint8Array, terrain: Uint8Array, visibility: Uint8Array,
  w: number, h: number, x: number, y: number, base = 0,
): readonly (readonly [number, number])[] {
  const i = x < 0 || y < 0 || x >= w || y >= h ? -1 : base + y * w + x;
  if (i < 0 || (terrain[i] ?? 0) !== WATER || (river[i] ?? 0) !== 0) return [];
  const out: [number, number][] = [];
  for (let k = 0; k < 8; k++) {
    const [dx, dy, bit] = ARM_DIRS[k]!;
    const nx = x + dx, ny = y + dy;
    if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
    const kind = river[base + ny * w + nx] ?? 0;
    if (kind === 0) continue;
    // the neighbour's mouth arm points back at us when it is the opposite bit
    const back = ARM_DIRS[(k + 4) % 8]![2];
    if (riverMouth(river, terrain, visibility, w, h, nx, ny, base) === back) out.push([kind, bit]);
  }
  return out;
}

// ---------------------------------------------------------------- corners

/**
 * The four corner terrains of display cell `(dy, dx)`, clamped at the map edge.
 *
 * Clamping rather than skipping is deliberate: display cells run 0..h and 0..w
 * inclusive, which is one more row and column than the world grid, so the
 * outermost half-tile ring is painted with the edge terrain instead of leaving
 * a hard seam where the map stops.
 */
export function displayCorners(
  terrain: Uint8Array, w: number, h: number, z: number, dy: number, dx: number,
): [number, number, number, number] {
  const plane = w * h * z;
  const cx0 = dx - 1 < 0 ? 0 : dx - 1 >= w ? w - 1 : dx - 1;
  const cx1 = dx < 0 ? 0 : dx >= w ? w - 1 : dx;
  const cy0 = dy - 1 < 0 ? 0 : dy - 1 >= h ? h - 1 : dy - 1;
  const cy1 = dy < 0 ? 0 : dy >= h ? h - 1 : dy;
  return [
    terrain[plane + cy0 * w + cx0] ?? 0,
    terrain[plane + cy0 * w + cx1] ?? 0,
    terrain[plane + cy1 * w + cx1] ?? 0,
    terrain[plane + cy1 * w + cx0] ?? 0,
  ];
}

/**
 * The same four corners, with unexplored ones replaced by an explored neighbour.
 *
 * `PlayerObservation` masks the terrain plane to ZERO outside explored ground,
 * and terrain index 0 is `water`. A display cell straddles four world cells, so
 * every cell on the fog boundary had one or two corners reading "deep water"
 * that no rule had put there: the map wore a one-tile ring of blue dither
 * around the whole explored blob, following the black staircase cell for cell,
 * and because water is the highest-chroma terrain in the set that ring was the
 * strongest colour accent on the screen - drawing the eye to the fog boundary
 * and reading as a broken alpha.
 *
 * An unexplored corner has no terrain, so it must not vote. Substituting the
 * majority explored terrain makes the boundary cell a plain fill of the land
 * that is actually there, and the fog veil - which cuts on the cell grid with
 * its own torn edge - is then the only thing that draws the boundary.
 *
 * All four unexplored is left alone: that cell is under solid black anyway.
 */
export function displayCornersVisible(
  terrain: Uint8Array, visibility: Uint8Array,
  w: number, h: number, z: number, dy: number, dx: number,
): [number, number, number, number] {
  const plane = w * h * z;
  const cx0 = dx - 1 < 0 ? 0 : dx - 1 >= w ? w - 1 : dx - 1;
  const cx1 = dx < 0 ? 0 : dx >= w ? w - 1 : dx;
  const cy0 = dy - 1 < 0 ? 0 : dy - 1 >= h ? h - 1 : dy - 1;
  const cy1 = dy < 0 ? 0 : dy >= h ? h - 1 : dy;
  const idx: readonly [number, number, number, number] = [
    plane + cy0 * w + cx0, plane + cy0 * w + cx1,
    plane + cy1 * w + cx1, plane + cy1 * w + cx0,
  ];
  const out: [number, number, number, number] = [
    terrain[idx[0]] ?? 0, terrain[idx[1]] ?? 0,
    terrain[idx[2]] ?? 0, terrain[idx[3]] ?? 0,
  ];

  let unseen = 0;
  for (let i = 0; i < 4; i++) if ((visibility[idx[i]!] ?? 0) === 0) unseen |= 1 << i;
  if (unseen === 0 || unseen === 15) return out;

  // Majority of the explored corners, ties broken by corner order, so the
  // substitution is a pure function of the cell and a re-bake is identical.
  let best = -1;
  let bestCount = 0;
  for (let i = 0; i < 4; i++) {
    if ((unseen & (1 << i)) !== 0) continue;
    let count = 0;
    for (let j = 0; j < 4; j++) {
      if ((unseen & (1 << j)) === 0 && out[j] === out[i]) count++;
    }
    if (count > bestCount) { bestCount = count; best = out[i]!; }
  }
  if (best < 0) return out;
  for (let i = 0; i < 4; i++) if ((unseen & (1 << i)) !== 0) out[i] = best;
  return out;
}

// ---------------------------------------------------------------- chunks

export interface ChunkCoord {
  readonly cx: number;
  readonly cy: number;
  readonly z: number;
}

export const chunkKey = (c: ChunkCoord): string => `${c.z}/${c.cy}/${c.cx}`;

/** Display-cell grid size: one more row and column than the world grid. */
export const displayGridSize = (w: number, h: number): { cols: number; rows: number } =>
  ({ cols: w + 1, rows: h + 1 });

export const chunkGridSize = (w: number, h: number): { cols: number; rows: number } => {
  const d = displayGridSize(w, h);
  return {
    cols: Math.ceil(d.cols / CHUNK_CELLS),
    rows: Math.ceil(d.rows / CHUNK_CELLS),
  };
};

/** Half-open display-cell range covered by a chunk, clipped to the grid. */
export function chunkDisplayRange(
  c: ChunkCoord, w: number, h: number,
): { dx0: number; dy0: number; dx1: number; dy1: number } {
  const d = displayGridSize(w, h);
  return {
    dx0: c.cx * CHUNK_CELLS,
    dy0: c.cy * CHUNK_CELLS,
    dx1: Math.min(d.cols, (c.cx + 1) * CHUNK_CELLS),
    dy1: Math.min(d.rows, (c.cy + 1) * CHUNK_CELLS),
  };
}

/**
 * Chunks a change to world cell `(x, y)` invalidates.
 *
 * A world cell is a corner of four display cells - it is the BR of (y,x), the
 * BL of (y,x+1), the TR of (y+1,x) and the TL of (y+1,x+1) - and those four can
 * straddle up to four chunks. Re-rendering only the chunk containing the cell
 * leaves a stale seam one display cell wide, which is exactly the bug this
 * function exists to prevent.
 */
export function chunksForWorldCell(x: number, y: number, z: number): ChunkCoord[] {
  const out: ChunkCoord[] = [];
  const seen = new Set<string>();
  for (const dy of [y, y + 1]) {
    for (const dx of [x, x + 1]) {
      const c: ChunkCoord = {
        cx: Math.floor(dx / CHUNK_CELLS),
        cy: Math.floor(dy / CHUNK_CELLS),
        z,
      };
      const k = chunkKey(c);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(c);
    }
  }
  return out;
}

/** Chunks overlapping a world-pixel rectangle, clipped to the map. */
export function chunksInPixelRect(
  rect: { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number },
  w: number, h: number, z: number, tile: number,
): ChunkCoord[] {
  const grid = chunkGridSize(w, h);
  const span = CHUNK_CELLS * tile;
  // Display cell (dy,dx) starts at world pixel (dx*tile - tile/2), so a chunk
  // starts half a tile earlier than its cell index suggests.
  const half = tile / 2;
  const cx0 = Math.max(0, Math.floor((rect.x0 + half) / span));
  const cy0 = Math.max(0, Math.floor((rect.y0 + half) / span));
  const cx1 = Math.min(grid.cols - 1, Math.floor((rect.x1 + half) / span));
  const cy1 = Math.min(grid.rows - 1, Math.floor((rect.y1 + half) / span));
  const out: ChunkCoord[] = [];
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) out.push({ cx, cy, z });
  }
  return out;
}

/**
 * World pixel of a chunk's top-left.
 *
 * Half a tile before the cell index suggests, because display cell (dy, dx)
 * starts at `(dx*tile - tile/2)` - the dual grid's half-tile offset, expressed
 * once here so nothing downstream has to remember it.
 */
export function chunkOrigin(c: ChunkCoord, tile: number): { x: number; y: number } {
  return {
    x: c.cx * CHUNK_CELLS * tile - tile / 2,
    y: c.cy * CHUNK_CELLS * tile - tile / 2,
  };
}
